import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  Banknote,
  CalendarDays,
  ChartLine,
  CircleDollarSign,
  FileSpreadsheet,
  Megaphone,
  Package,
  Receipt,
  RefreshCw,
  Store,
  Truck,
  TrendingUp,
  UserCheck,
  Users,
} from "lucide-react";
import { useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { KpiCard } from "@/components/ui/KpiCard";
import { PageHeader } from "@/components/ui/PageHeader";
import { MultiSelect } from "@/components/ui/MultiSelect";
import { api } from "@/lib/api";
import clsx from "clsx";

type Item = {
  variation_id: string;
  product_id: number | null;
  name: string;
  total_qty: number;
  n_orders: number;
  n_returns: number;
  return_rate: number;
  revenue: number;
  cost_price: number;
  cogs: number;
  ship_cost: number;
  marketing_alloc: number;
  gross_profit: number;
  net_profit: number;
  gross_margin_pct: number;
  net_margin_pct: number;
  has_cost: boolean;
};

type ShopBreakdown = {
  shop_id: number;
  shop_name: string;
  shop_code: string;
  n_orders: number;
  revenue: number;
  ship_cost: number;
};

type PnlResult = {
  date_from: string;
  date_to: string;
  shop_id: number | null;
  total_revenue: number;
  total_cogs: number;
  total_ship_cost: number;
  total_marketing: number;
  total_gross_profit: number;
  total_net_profit: number;
  n_products_tracked: number;
  n_products_missing_cost: number;
  items: Item[];
  by_shop: ShopBreakdown[];
};

type MonthlyDay = {
  date: string;
  n_total: number;
  n_shipped: number;
  n_received: number;             // đã nhận NORMAL (không tính hoàn 1 phần)
  n_partial_return: number;       // đơn có tag "Hoàn một phần"
  n_returning: number;
  n_returned: number;
  ship_cost: number;              // tổng, đã tính 2 chiều cho đơn hoàn 1 phần
  revenue: number;                // doanh thu (đơn hoàn 1 phần chỉ tính 20K default)
  cod_total: number;
  returned_value: number;
  partial_ship_cost: number;      // phí ship của riêng nhóm hoàn 1 phần (2 chiều)
  marketing_cost: number;         // đã cộng thuế NT 10%
  marketing_cost_raw: number;     // chưa cộng thuế
  return_rate: number;
};

type OrderDetail = {
  pos_order_id: string;
  partner_extend_code: string | null;
  partner_name: string | null;
  date: string | null;
  status: number;
  status_name: string | null;
  cod: number;
  partner_fee: number;
  customer: string | null;
  items: string | null;
  shop_name: string;
  is_partial_return: boolean;
};

type MonthlyResult = {
  year: number;
  month: number;
  shop_id: number | null;
  marketing_source: "ads_live" | "invoices";
  days: MonthlyDay[];
  totals: MonthlyDay;
  order_details: OrderDetail[];
  suggested_month?: { year: number; month: number; n_orders: number };
};

type ProductCodeLite = {
  id: number;
  code: string;
  name: string;
  n_shops: number;
};

const fmt = (v: number) => Math.round(v).toLocaleString("vi-VN") + "đ";
const fmtN = (v: number) =>
  v === 0 ? "—" : Math.round(v).toLocaleString("vi-VN");
const fmtNumOnly = (v: number) => v.toLocaleString("vi-VN");
const fmtShort = (v: number) => {
  const abs = Math.abs(v);
  if (abs >= 1_000_000_000) return (v / 1_000_000_000).toFixed(1) + "B";
  if (abs >= 1_000_000) return (v / 1_000_000).toFixed(1) + "M";
  if (abs >= 1_000) return (v / 1_000).toFixed(0) + "K";
  return Math.round(v).toString();
};

const now = new Date();
const startOfYear = () => now.getFullYear() + "-01-01";
const today = () => now.toISOString().slice(0, 10);

const STATUS_MAP: Record<number, { label: string; tone: string }> = {
  0: { label: "Mới", tone: "badge-neutral" },
  1: { label: "Đã xác nhận", tone: "badge-brand" },
  2: { label: "Đã gửi hàng", tone: "badge-brand" },
  3: { label: "Đã nhận", tone: "badge-success" },
  4: { label: "Đang hoàn", tone: "badge-warning" },
  5: { label: "Đã hoàn", tone: "badge-danger" },
  6: { label: "Đã huỷ", tone: "badge-danger" },
  7: { label: "Đã xoá", tone: "badge-danger" },
  8: { label: "Đang đóng", tone: "badge-neutral" },
  9: { label: "Chờ chuyển", tone: "badge-neutral" },
  11: { label: "Chờ hàng", tone: "badge-neutral" },
  12: { label: "Chờ in", tone: "badge-neutral" },
  13: { label: "Đã in", tone: "badge-neutral" },
  16: { label: "Đã thu tiền", tone: "badge-success" },
  17: { label: "Chờ xác nhận", tone: "badge-neutral" },
  20: { label: "Đã đặt", tone: "badge-neutral" },
};

type SortKey =
  | "revenue"
  | "net_profit"
  | "gross_profit"
  | "net_margin_pct"
  | "return_rate"
  | "total_qty";

type Tab = "monthly" | "pnl" | "orders" | "staff";

type VariationOpt = {
  variation_id: string;
  detail: string;
  n_orders: number;
  qty: number;
};

type ProductOpt = {
  name: string;
  n_orders: number;
  qty: number;
  variations: VariationOpt[];
};

const CARRIER_OPTIONS = ["VTP", "Shopee Xpress", "J&T", "Best Inc"];

export function ProductPnlPage() {
  const [tab, setTab] = useState<Tab>("monthly");
  const [codeId, setCodeId] = useState<number | "">("");
  const [carrier, setCarrier] = useState<string>("");
  // Lựa chọn thô trên UI (chọn nhiều)
  const [selProductNames, setSelProductNames] = useState<string[]>([]);
  const [selVariationIds, setSelVariationIds] = useState<string[]>([]);
  const [month, setMonth] = useState<string>(
    `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`
  );
  const [year, monthStr] = month.split("-");

  // For PnL tab
  const [dateFrom, setDateFrom] = useState(startOfYear());
  const [dateTo, setDateTo] = useState(today());
  const [sortKey, setSortKey] = useState<SortKey>("net_profit");
  const [sortDesc, setSortDesc] = useState(true);

  const codesQ = useQuery({
    queryKey: ["product-codes"],
    queryFn: async () =>
      (await api.get<ProductCodeLite[]>("/product-codes")).data,
  });

  const prodOptsQ = useQuery({
    queryKey: ["product-options", codeId],
    queryFn: async () =>
      (
        await api.get<{ products: ProductOpt[] }>("/reports/product-options", {
          params: codeId ? { product_code_id: codeId } : {},
        })
      ).data.products,
  });
  const prodByName = useMemo(
    () => new Map((prodOptsQ.data ?? []).map((p) => [p.name, p])),
    [prodOptsQ.data]
  );
  // Phân loại của các SP đang chọn, gom nhóm theo tên SP
  const variationOptions = selProductNames.flatMap((n) =>
    (prodByName.get(n)?.variations ?? []).map((v) => ({
      value: v.variation_id,
      label: v.detail || "(không phân loại)",
      count: v.n_orders,
      group: n,
    }))
  );
  // Bộ lọc gửi lên API: SP đã chọn phân loại riêng → chỉ lọc các phân loại đó,
  // SP còn lại → lấy mọi phân loại (gửi theo tên SP).
  const variationIds = selVariationIds;
  const productNames = selProductNames.filter(
    (n) =>
      !(prodByName.get(n)?.variations ?? []).some((v) =>
        selVariationIds.includes(v.variation_id)
      )
  );
  const changeProducts = (names: string[]) => {
    setSelProductNames(names);
    // bỏ các phân loại thuộc SP vừa bỏ chọn
    const keep = new Set(
      names.flatMap((n) =>
        (prodByName.get(n)?.variations ?? []).map((v) => v.variation_id)
      )
    );
    setSelVariationIds((ids) => ids.filter((id) => keep.has(id)));
  };

  const [exporting, setExporting] = useState(false);
  const exportXlsx = async () => {
    setExporting(true);
    try {
      const params: Record<string, string | number | string[]> = {
        year: parseInt(year),
        month: parseInt(monthStr),
        date_from: dateFrom,
        date_to: dateTo,
      };
      if (codeId) params.product_code_id = codeId;
      if (carrier) params.carrier = carrier;
      if (productNames.length) params.product_name = productNames;
      if (variationIds.length) params.variation_id = variationIds;
      const r = await api.get("/reports/product-pnl.xlsx", {
        paramsSerializer: { indexes: null },
        params,
        responseType: "blob",
      });
      const blob = new Blob([r.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const code = codesQ.data?.find((c) => c.id === codeId)?.code || "all";
      const a = document.createElement("a");
      a.href = url;
      a.download = `TheoDoiKinhDoanh_${year}-${monthStr}_${code}.xlsx`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (e: any) {
      alert(`Lỗi xuất Excel: ${e?.response?.data?.detail || e?.message || "unknown"}`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <PageHeader
        title="Theo dõi kinh doanh"
        subtitle="Thống kê đơn hàng theo shop + tháng · lãi lỗ per SP · chi tiết đơn"
        icon={CircleDollarSign}
        actions={
          <button
            onClick={exportXlsx}
            disabled={exporting}
            className="btn btn-primary btn-sm flex items-center gap-2"
            title="Tải Excel 4 sheets: Thống kê tháng, Chi tiết đơn, Lãi lỗ SP, Hiệu suất NV"
          >
            <FileSpreadsheet className="w-4 h-4" />
            {exporting ? "Đang tạo…" : "Xuất Excel"}
          </button>
        }
      />

      {/* Mã hàng + period selector — shared across tabs */}
      <Card className="mb-4">
        <CardBody className="flex flex-wrap items-end gap-3">
          <div>
            <label className="label">Mã hàng (gộp N shop)</label>
            <select
              value={codeId}
              onChange={(e) => {
                setCodeId(e.target.value ? Number(e.target.value) : "");
                // DS sản phẩm lọc theo mã hàng → SP đang chọn có thể không còn
                setSelProductNames([]);
                setSelVariationIds([]);
              }}
              className="input w-72"
            >
              <option value="">— Tất cả mã hàng —</option>
              {codesQ.data?.map((c) => (
                <option key={c.id} value={c.id}>
                  [{c.code}] {c.name} ({c.n_shops} shop)
                </option>
              ))}
            </select>
            {codesQ.data?.length === 0 && (
              <a
                href="/ma-hang"
                className="text-[10px] text-brand-600 underline mt-1 inline-block"
              >
                Chưa có mã hàng — tạo tại /ma-hang
              </a>
            )}
          </div>
          {tab === "monthly" || tab === "orders" ? (
            <>
              <div>
                <label className="label">Tháng</label>
                <input
                  type="month"
                  value={month}
                  onChange={(e) => setMonth(e.target.value)}
                  className="input w-40"
                />
              </div>
              <div>
                <label className="label">ĐVVC</label>
                <select
                  value={carrier}
                  onChange={(e) => setCarrier(e.target.value)}
                  className="input w-40"
                >
                  <option value="">— Tất cả —</option>
                  {CARRIER_OPTIONS.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Sản phẩm</label>
                <MultiSelect
                  options={(prodOptsQ.data ?? []).map((pr) => ({
                    value: pr.name,
                    label: pr.name,
                    count: pr.n_orders,
                  }))}
                  value={selProductNames}
                  onChange={changeProducts}
                  placeholder="— Tất cả sản phẩm —"
                  unit="sản phẩm"
                  loading={prodOptsQ.isLoading}
                  className="w-64"
                />
              </div>
              <div>
                <label className="label">Phân loại</label>
                <MultiSelect
                  options={variationOptions}
                  value={selVariationIds}
                  onChange={setSelVariationIds}
                  placeholder="— Tất cả phân loại —"
                  unit="phân loại"
                  disabled={selProductNames.length === 0}
                  disabledText="— Chọn sản phẩm trước —"
                  className="w-56"
                />
              </div>
              {selProductNames.length > 0 && (
                <button
                  onClick={() => {
                    setSelProductNames([]);
                    setSelVariationIds([]);
                  }}
                  className="btn-ghost text-xs mb-1.5"
                >
                  ✕ Bỏ lọc SP
                </button>
              )}
            </>
          ) : (
            <>
              <div>
                <label className="label">Từ ngày</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="input w-40"
                />
              </div>
              <div>
                <label className="label">Đến ngày</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="input w-40"
                />
              </div>
            </>
          )}
        </CardBody>
      </Card>

      {/* Tabs */}
      <div className="border-b border-slate-200 mb-4 flex gap-1">
        <TabBtn
          active={tab === "monthly"}
          onClick={() => setTab("monthly")}
          icon={CalendarDays}
        >
          Thống kê tháng
        </TabBtn>
        <TabBtn
          active={tab === "pnl"}
          onClick={() => setTab("pnl")}
          icon={ChartLine}
        >
          Lãi lỗ SP
        </TabBtn>
        <TabBtn
          active={tab === "orders"}
          onClick={() => setTab("orders")}
          icon={Receipt}
        >
          Chi tiết đơn
        </TabBtn>
        <TabBtn
          active={tab === "staff"}
          onClick={() => setTab("staff")}
          icon={UserCheck}
        >
          Hiệu suất NV
        </TabBtn>
      </div>

      {tab === "monthly" && (
        <MonthlyTab
          codeId={codeId || null}
          carrier={carrier || null}
          productNames={productNames}
          variationIds={variationIds}
          year={parseInt(year)}
          month={parseInt(monthStr)}
          onGoToMonth={(y, m) =>
            setMonth(`${y}-${String(m).padStart(2, "0")}`)
          }
        />
      )}
      {tab === "pnl" && (
        <PnlTab
          codeId={codeId || null}
          dateFrom={dateFrom}
          dateTo={dateTo}
          sortKey={sortKey}
          setSortKey={setSortKey}
          sortDesc={sortDesc}
          setSortDesc={setSortDesc}
        />
      )}
      {tab === "orders" && (
        <OrdersTab
          codeId={codeId || null}
          carrier={carrier || null}
          productNames={productNames}
          variationIds={variationIds}
          year={parseInt(year)}
          month={parseInt(monthStr)}
        />
      )}
      {tab === "staff" && (
        <StaffTab
          codeId={codeId || null}
          year={parseInt(year)}
          month={parseInt(monthStr)}
        />
      )}
    </div>
  );
}

function TabBtn({
  active,
  onClick,
  icon: Icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon: any;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        "px-4 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-1.5 transition-colors",
        active
          ? "border-brand-600 text-brand-700"
          : "border-transparent text-slate-500 hover:text-slate-900"
      )}
    >
      <Icon size={14} />
      {children}
    </button>
  );
}

// ═════════════════════════════════════════════════════════════
// TAB 1: Monthly stats (Google Sheets style)
// ═════════════════════════════════════════════════════════════

function MonthlyTab({
  codeId,
  carrier,
  productNames,
  variationIds,
  year,
  month,
  onGoToMonth,
}: {
  codeId: number | null;
  carrier: string | null;
  productNames: string[];
  variationIds: string[];
  year: number;
  month: number;
  onGoToMonth: (year: number, month: number) => void;
}) {
  const q = useQuery({
    queryKey: [
      "shop-monthly", codeId, carrier, year, month, productNames, variationIds,
    ],
    queryFn: async () =>
      (
        await api.get<MonthlyResult>("/reports/shop-monthly", {
          paramsSerializer: { indexes: null },
          params: {
            year,
            month,
            ...(codeId ? { product_code_id: codeId } : {}),
            ...(carrier ? { carrier } : {}),
            ...(productNames.length ? { product_name: productNames } : {}),
            ...(variationIds.length ? { variation_id: variationIds } : {}),
          },
        })
      ).data,
  });

  if (q.isLoading) {
    return <div className="text-slate-500 py-8 text-center">Đang tải...</div>;
  }
  if (!q.data) return null;
  const { days, totals } = q.data;
  const suggested = q.data.suggested_month;

  return (
    <>
      {days.length === 0 && suggested && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm flex items-center justify-between">
          <div className="text-amber-900">
            <b>Không có đơn</b> nào được đẩy trong tháng{" "}
            {String(month).padStart(2, "0")}/{year}. Có{" "}
            <b>{suggested.n_orders} đơn</b> trong tháng{" "}
            <b>{String(suggested.month).padStart(2, "0")}/{suggested.year}</b>.
          </div>
          <button
            onClick={() => onGoToMonth(suggested.year, suggested.month)}
            className="btn-primary text-xs"
          >
            → Chuyển sang tháng {String(suggested.month).padStart(2, "0")}/
            {suggested.year}
          </button>
        </div>
      )}

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-4">
        <KpiCard
          label="Tổng đơn"
          value={fmtNumOnly(totals.n_total)}
          hint="đi hàng trong tháng"
          icon={Receipt}
          tone="brand"
        />
        <KpiCard
          label="Đã gửi hàng"
          value={fmtNumOnly(totals.n_shipped)}
          icon={Truck}
          tone="slate"
        />
        <KpiCard
          label="Đã nhận"
          value={fmtNumOnly(totals.n_received)}
          hint="không tính hoàn 1 phần"
          icon={TrendingUp}
          tone="green"
        />
        <KpiCard
          label="Hoàn 1 phần"
          value={fmtNumOnly(totals.n_partial_return)}
          hint="COD 20K · phí 2 chặng (đi + hoàn)"
          icon={ArrowDownRight}
          tone="amber"
        />
        <KpiCard
          label="Đã hoàn"
          value={fmtNumOnly(totals.n_returned)}
          hint={`${(totals.return_rate * 100).toFixed(1)}% tỷ lệ hoàn`}
          icon={ArrowDownRight}
          tone={totals.return_rate > 0.1 ? "red" : "amber"}
        />
        <KpiCard
          label="Doanh thu"
          value={fmtShort(totals.revenue)}
          hint="đã nhận + hoàn 1 phần (20K)"
          icon={Banknote}
          tone="green"
        />
        <KpiCard
          label="Phí ship"
          value={fmtShort(totals.ship_cost)}
          hint="từ bảng kê VTP"
          icon={Truck}
          tone="slate"
        />
        <KpiCard
          label="Chi phí QC"
          value={fmtShort(totals.marketing_cost)}
          hint={
            q.data.marketing_source === "ads_live"
              ? `Meta live · +10% thuế NT`
              : "Từ hoá đơn"
          }
          icon={Megaphone}
          tone="amber"
        />
      </div>

      {/* Daily table (Google Sheets style) */}
      <Card>
        <CardHeader
          title={`Thống kê tháng ${String(month).padStart(2, "0")}/${year}`}
          subtitle={
            q.data.marketing_source === "ads_live"
              ? "Mốc ngày = ngày đi hàng (chuyển sang Đã gửi hàng). Phí ship từ bảng kê VTP. Chi phí QC live từ Meta ads (đã cộng thuế NT 10%, số trong ngoặc chưa cộng)."
              : "Mốc ngày = ngày đi hàng (chuyển sang Đã gửi hàng). Phí ship từ bảng kê VTP. Chi phí QC phân bổ theo tỉ trọng doanh thu từ hoá đơn."
          }
        />
        <div className="overflow-x-auto scrollbar-thin">
          <table className="table-modern">
            <thead>
              <tr>
                <th className="min-w-[100px]">Ngày</th>
                <th className="text-right">Tổng đơn</th>
                <th className="text-right">Đã gửi</th>
                <th className="text-right">Đã nhận</th>
                <th className="text-right">Hoàn 1 phần</th>
                <th className="text-right">Đang hoàn</th>
                <th className="text-right">Đã hoàn</th>
                <th className="text-right">Phí ship</th>
                <th className="text-right">Chi phí QC</th>
                <th className="text-right">Doanh thu</th>
                <th className="text-right">Tiền COD</th>
                <th className="text-right">Giá trị hoàn</th>
                <th className="text-right">Tỷ lệ hoàn</th>
              </tr>
            </thead>
            <tbody>
              {days.length === 0 && (
                <tr>
                  <td colSpan={13} className="text-center py-8 text-slate-500">
                    Không có đơn trong tháng này
                  </td>
                </tr>
              )}
              {days.map((d) => (
                <tr key={d.date}>
                  <td className="font-medium">
                    {new Date(d.date + "T00:00:00").toLocaleDateString("vi-VN")}
                  </td>
                  <td className="text-right font-mono">{fmtN(d.n_total)}</td>
                  <td className="text-right font-mono text-slate-600">
                    {fmtN(d.n_shipped)}
                  </td>
                  <td className="text-right font-mono text-emerald-700 font-semibold">
                    {fmtN(d.n_received)}
                  </td>
                  <td className="text-right font-mono text-amber-600">
                    {fmtN(d.n_partial_return)}
                  </td>
                  <td className="text-right font-mono text-amber-700">
                    {fmtN(d.n_returning)}
                  </td>
                  <td className="text-right font-mono text-red-600">
                    {fmtN(d.n_returned)}
                  </td>
                  <td className="text-right font-mono text-slate-600">
                    {d.ship_cost > 0 ? fmt(d.ship_cost) : "—"}
                  </td>
                  <td className="text-right font-mono text-amber-700">
                    {d.marketing_cost > 0 ? (
                      <>
                        <div>{fmt(d.marketing_cost)}</div>
                        {d.marketing_cost_raw > 0 &&
                          Math.abs(d.marketing_cost - d.marketing_cost_raw) > 1 && (
                            <div
                              className="text-[9px] text-slate-400"
                              title="Chưa cộng thuế nhà thầu 10%"
                            >
                              ({fmt(d.marketing_cost_raw)})
                            </div>
                          )}
                      </>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="text-right font-mono font-semibold text-emerald-700">
                    {d.revenue > 0 ? fmt(d.revenue) : "—"}
                  </td>
                  <td className="text-right font-mono text-slate-600">
                    {d.cod_total > 0 ? fmt(d.cod_total) : "—"}
                  </td>
                  <td className="text-right font-mono text-red-600">
                    {d.returned_value > 0 ? fmt(d.returned_value) : "—"}
                  </td>
                  <td
                    className={clsx(
                      "text-right font-mono text-xs",
                      d.return_rate > 0.1
                        ? "text-red-600 font-semibold"
                        : "text-slate-500"
                    )}
                  >
                    {(d.return_rate * 100).toFixed(1)}%
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-slate-200 bg-slate-50 font-semibold">
                <td className="text-slate-900">TỔNG</td>
                <td className="text-right font-mono">
                  {fmtN(totals.n_total)}
                </td>
                <td className="text-right font-mono">
                  {fmtN(totals.n_shipped)}
                </td>
                <td className="text-right font-mono text-emerald-700">
                  {fmtN(totals.n_received)}
                </td>
                <td className="text-right font-mono text-amber-600">
                  {fmtN(totals.n_partial_return)}
                </td>
                <td className="text-right font-mono text-amber-700">
                  {fmtN(totals.n_returning)}
                </td>
                <td className="text-right font-mono text-red-600">
                  {fmtN(totals.n_returned)}
                </td>
                <td className="text-right font-mono">
                  {fmt(totals.ship_cost)}
                </td>
                <td className="text-right font-mono text-amber-700">
                  <div>{fmt(totals.marketing_cost || 0)}</div>
                  {totals.marketing_cost_raw > 0 &&
                    Math.abs(totals.marketing_cost - totals.marketing_cost_raw) >
                      1 && (
                      <div
                        className="text-[9px] text-slate-400 font-normal"
                        title="Chưa cộng thuế nhà thầu 10%"
                      >
                        ({fmt(totals.marketing_cost_raw)})
                      </div>
                    )}
                </td>
                <td className="text-right font-mono text-emerald-700">
                  {fmt(totals.revenue)}
                </td>
                <td className="text-right font-mono">{fmt(totals.cod_total)}</td>
                <td className="text-right font-mono text-red-600">
                  {fmt(totals.returned_value)}
                </td>
                <td className="text-right font-mono">
                  {(totals.return_rate * 100).toFixed(1)}%
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </Card>
    </>
  );
}

// ═════════════════════════════════════════════════════════════
// TAB 2: PnL (lãi lỗ per SP — moved existing content here)
// ═════════════════════════════════════════════════════════════

function PnlTab({
  codeId,
  dateFrom,
  dateTo,
  sortKey,
  setSortKey,
  sortDesc,
  setSortDesc,
}: {
  codeId: number | null;
  dateFrom: string;
  dateTo: string;
  sortKey: SortKey;
  setSortKey: (k: SortKey) => void;
  sortDesc: boolean;
  setSortDesc: (b: boolean) => void;
}) {
  const [groupBy, setGroupBy] = useState<"variation" | "product_code">(
    "product_code"
  );

  const q = useQuery({
    queryKey: ["product-pnl", dateFrom, dateTo, codeId, groupBy],
    queryFn: async () =>
      (
        await api.get<PnlResult>("/reports/product-pnl", {
          params: {
            date_from: dateFrom,
            date_to: dateTo,
            group_by: groupBy,
            ...(codeId ? { product_code_id: codeId } : {}),
          },
        })
      ).data,
  });

  const sortedItems = useMemo(() => {
    if (!q.data) return [];
    const arr = [...q.data.items];
    arr.sort((a, b) => {
      const va = a[sortKey] ?? 0;
      const vb = b[sortKey] ?? 0;
      return sortDesc ? vb - va : va - vb;
    });
    return arr;
  }, [q.data, sortKey, sortDesc]);

  const clickSort = (k: SortKey) => {
    if (k === sortKey) setSortDesc(!sortDesc);
    else {
      setSortKey(k);
      setSortDesc(true);
    }
  };

  const arrow = (k: SortKey) => (sortKey === k ? (sortDesc ? " ↓" : " ↑") : "");

  const top10 = useMemo(
    () => sortedItems.slice(0, 10).reverse(),
    [sortedItems]
  );

  if (q.isLoading) {
    return <div className="text-slate-500 py-8 text-center">Đang tính...</div>;
  }
  if (!q.data) return null;

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
        <KpiCard label="Doanh thu" value={fmtShort(q.data.total_revenue)} icon={Banknote} tone="brand" />
        <KpiCard label="Giá vốn" value={fmtShort(q.data.total_cogs)} icon={Package} tone="slate" />
        <KpiCard label="Phí ship" value={fmtShort(q.data.total_ship_cost)} icon={Truck} tone="slate" />
        <KpiCard label="Marketing" value={fmtShort(q.data.total_marketing)} icon={Megaphone} tone="amber" />
        <KpiCard
          label="Lãi gộp"
          value={fmtShort(q.data.total_gross_profit)}
          icon={q.data.total_gross_profit >= 0 ? ArrowUpRight : ArrowDownRight}
          tone={q.data.total_gross_profit >= 0 ? "green" : "red"}
        />
        <KpiCard
          label="Lãi ròng"
          value={fmtShort(q.data.total_net_profit)}
          hint={
            q.data.total_revenue > 0
              ? `${((q.data.total_net_profit / q.data.total_revenue) * 100).toFixed(1)}% margin`
              : undefined
          }
          icon={TrendingUp}
          tone={q.data.total_net_profit >= 0 ? "green" : "red"}
        />
      </div>

      {q.data.n_products_missing_cost > 0 && (
        <div className="mb-4 flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <div>
            <b>{q.data.n_products_missing_cost}</b>/{q.data.n_products_tracked} SP chưa có giá vốn — Lãi lỗ đang PHÓNG ĐẠI.{" "}
            <a href="/san-pham" className="text-brand-700 underline underline-offset-2 font-medium">
              Nhập giá vốn
            </a>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <Card className="lg:col-span-2">
          <CardHeader title="Top 10 SP theo lãi ròng" subtitle="Đỏ = lỗ · Xanh = lãi" />
          <CardBody>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={top10} layout="vertical" margin={{ top: 4, right: 24, left: 0, bottom: 4 }}>
                <CartesianGrid horizontal={false} stroke="#e2e8f0" />
                <XAxis type="number" tickFormatter={fmtShort} tick={{ fill: "#64748b", fontSize: 11 }} axisLine={{ stroke: "#e2e8f0" }} tickLine={false} />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={160}
                  tick={{ fill: "#334155", fontSize: 11 }}
                  axisLine={{ stroke: "#e2e8f0" }}
                  tickLine={false}
                  tickFormatter={(v: string) => (v.length > 22 ? v.slice(0, 22) + "…" : v)}
                />
                <Tooltip
                  cursor={{ fill: "rgba(99,102,241,0.06)" }}
                  contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", boxShadow: "0 4px 12px rgba(0,0,0,0.08)", fontSize: 12 }}
                  formatter={(v: number) => [fmt(v), "Lãi ròng"]}
                />
                <Bar dataKey="net_profit" radius={[0, 4, 4, 0]} barSize={16}>
                  {top10.map((it, i) => (
                    <Cell key={i} fill={it.net_profit >= 0 ? "#10b981" : "#ef4444"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Doanh thu theo shop" subtitle={`${q.data.by_shop.length} shop`} />
          <CardBody>
            {q.data.by_shop.length === 0 ? (
              <div className="text-sm text-slate-500 py-8 text-center">Không có đơn</div>
            ) : (
              <div className="space-y-2.5">
                {q.data.by_shop.map((s) => {
                  const maxRev = Math.max(...q.data!.by_shop.map((x) => x.revenue));
                  const pct = maxRev > 0 ? (s.revenue / maxRev) * 100 : 0;
                  return (
                    <div key={s.shop_id}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="flex items-center gap-1.5 font-medium text-slate-700">
                          <Store size={12} className="text-brand-600" />
                          {s.shop_name}
                        </span>
                        <span className="text-slate-500 font-mono">{fmtShort(s.revenue)}đ</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-brand-500 rounded-full" style={{ width: pct + "%" }} />
                      </div>
                      <div className="text-[10px] text-slate-500 mt-0.5">
                        {s.n_orders} đơn · ship {fmtShort(s.ship_cost)}đ
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardHeader
          title="Chi tiết lãi lỗ"
          subtitle={`${sortedItems.length} ${groupBy === "product_code" ? "mã hàng" : "variation"} · click header để sort`}
          action={
            <div className="inline-flex rounded-md border border-slate-200 overflow-hidden text-xs">
              <button
                onClick={() => setGroupBy("product_code")}
                className={clsx(
                  "px-2.5 py-1 transition-colors",
                  groupBy === "product_code"
                    ? "bg-brand-600 text-white"
                    : "bg-white text-slate-600 hover:bg-slate-50"
                )}
              >
                Theo mã hàng
              </button>
              <button
                onClick={() => setGroupBy("variation")}
                className={clsx(
                  "px-2.5 py-1 border-l border-slate-200 transition-colors",
                  groupBy === "variation"
                    ? "bg-brand-600 text-white"
                    : "bg-white text-slate-600 hover:bg-slate-50"
                )}
              >
                Theo variation
              </button>
            </div>
          }
        />
        <div className="overflow-x-auto scrollbar-thin">
          <table className="table-modern">
            <thead>
              <tr>
                <th>Sản phẩm</th>
                <th className="text-right cursor-pointer hover:text-slate-900" onClick={() => clickSort("total_qty")}>SL{arrow("total_qty")}</th>
                <th className="text-right">Đơn</th>
                <th className="text-right cursor-pointer hover:text-slate-900" onClick={() => clickSort("return_rate")}>Hoàn{arrow("return_rate")}</th>
                <th className="text-right cursor-pointer hover:text-slate-900" onClick={() => clickSort("revenue")}>Doanh thu{arrow("revenue")}</th>
                <th className="text-right">Giá vốn/SP</th>
                <th className="text-right">Tổng vốn</th>
                <th className="text-right">Ship</th>
                <th className="text-right">Marketing</th>
                <th className="text-right cursor-pointer hover:text-slate-900" onClick={() => clickSort("gross_profit")}>Lãi gộp{arrow("gross_profit")}</th>
                <th className="text-right cursor-pointer hover:text-slate-900" onClick={() => clickSort("net_profit")}>Lãi ròng{arrow("net_profit")}</th>
                <th className="text-right cursor-pointer hover:text-slate-900" onClick={() => clickSort("net_margin_pct")}>%{arrow("net_margin_pct")}</th>
              </tr>
            </thead>
            <tbody>
              {sortedItems.map((it) => (
                <tr key={it.variation_id} className={it.net_profit < 0 ? "bg-red-50/30" : ""}>
                  <td className="max-w-xs">
                    <div className="font-medium truncate text-slate-900" title={it.name}>{it.name}</div>
                    {!it.has_cost && <span className="badge-danger">chưa có giá vốn</span>}
                  </td>
                  <td className="text-right font-mono">{it.total_qty.toLocaleString("vi-VN")}</td>
                  <td className="text-right font-mono text-slate-500">{it.n_orders}</td>
                  <td className={`text-right font-mono ${it.return_rate > 0.1 ? "text-red-600 font-semibold" : "text-slate-500"}`}>
                    {it.n_returns}·{(it.return_rate * 100).toFixed(0)}%
                  </td>
                  <td className="text-right font-mono font-semibold">{fmt(it.revenue)}</td>
                  <td className="text-right font-mono text-slate-500">{it.cost_price ? fmt(it.cost_price) : "—"}</td>
                  <td className="text-right font-mono text-slate-500">{fmt(it.cogs)}</td>
                  <td className="text-right font-mono text-slate-500">{fmt(it.ship_cost)}</td>
                  <td className="text-right font-mono text-slate-500">{fmt(it.marketing_alloc)}</td>
                  <td className={`text-right font-mono font-semibold ${it.gross_profit >= 0 ? "text-emerald-700" : "text-red-600"}`}>{fmt(it.gross_profit)}</td>
                  <td className={`text-right font-mono font-semibold ${it.net_profit >= 0 ? "text-emerald-700" : "text-red-600"}`}>{fmt(it.net_profit)}</td>
                  <td className={`text-right font-mono ${it.net_margin_pct >= 0 ? "text-emerald-700" : "text-red-600"}`}>{it.net_margin_pct.toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}

// ═════════════════════════════════════════════════════════════
// TAB 3: Order details (per shop / month)
// ═════════════════════════════════════════════════════════════

function OrdersTab({
  codeId,
  carrier,
  productNames,
  variationIds,
  year,
  month,
}: {
  codeId: number | null;
  carrier: string | null;
  productNames: string[];
  variationIds: string[];
  year: number;
  month: number;
}) {
  const [filterDate, setFilterDate] = useState<string>("");
  const [filterStatus, setFilterStatus] = useState<string>("");

  const q = useQuery({
    queryKey: [
      "shop-monthly", codeId, carrier, year, month, productNames, variationIds,
    ],
    queryFn: async () =>
      (
        await api.get<MonthlyResult>("/reports/shop-monthly", {
          paramsSerializer: { indexes: null },
          params: {
            year,
            month,
            ...(codeId ? { product_code_id: codeId } : {}),
            ...(carrier ? { carrier } : {}),
            ...(productNames.length ? { product_name: productNames } : {}),
            ...(variationIds.length ? { variation_id: variationIds } : {}),
          },
        })
      ).data,
  });

  const orders = q.data?.order_details ?? [];

  // FULL list trạng thái POScake (không chỉ hiện có trong data)
  const allStatuses = useMemo(() => {
    // Đếm số lượng đơn per status để hiển thị bên cạnh label
    const cnt = new Map<number, number>();
    orders.forEach((o) => cnt.set(o.status, (cnt.get(o.status) ?? 0) + 1));
    // Sort theo thứ tự flow POScake logic
    const order = [0, 17, 1, 8, 9, 11, 12, 13, 20, 2, 3, 16, 4, 15, 5, 6, 7];
    const shown = new Set([...Object.keys(STATUS_MAP).map(Number), ...cnt.keys()]);
    return Array.from(shown)
      .sort((a, b) => order.indexOf(a) - order.indexOf(b))
      .map((s) => ({ code: s, count: cnt.get(s) ?? 0 }));
  }, [orders]);

  // Danh sách ngày có đơn — để populate select "Ngày"
  const availableDates = useMemo(() => {
    const set = new Set<string>();
    orders.forEach((o) => {
      if (o.date) set.add(o.date);
    });
    return Array.from(set).sort().reverse();
  }, [orders]);

  const partialCount = useMemo(
    () => orders.filter((o) => o.is_partial_return).length,
    [orders]
  );

  const filtered = useMemo(() => {
    return orders.filter((o) => {
      if (filterDate && o.date !== filterDate) return false;
      if (filterStatus === "partial") {
        if (!o.is_partial_return) return false;
      } else if (filterStatus !== "" && String(o.status) !== filterStatus) {
        return false;
      }
      return true;
    });
  }, [orders, filterDate, filterStatus]);

  const totalCod = useMemo(
    () => filtered.reduce((s, o) => s + o.cod, 0),
    [filtered]
  );

  if (q.isLoading) {
    return <div className="text-slate-500 py-8 text-center">Đang tải...</div>;
  }
  if (!q.data) return null;

  return (
    <>
      {/* Filter bar */}
      <Card className="mb-3">
        <CardBody className="flex flex-wrap items-end gap-3">
          <div>
            <label className="label">Lọc theo ngày</label>
            <select
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              className="input w-44"
            >
              <option value="">— Cả tháng —</option>
              {availableDates.map((d) => (
                <option key={d} value={d}>
                  {new Date(d + "T00:00:00").toLocaleDateString("vi-VN")}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Trạng thái</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="input w-56"
            >
              <option value="">— Tất cả trạng thái —</option>
              {partialCount > 0 && (
                <option value="partial">Hoàn 1 phần ({partialCount})</option>
              )}
              {allStatuses.map((s) => (
                <option key={s.code} value={s.code}>
                  {STATUS_MAP[s.code]?.label ?? `#${s.code}`}
                  {s.count > 0 ? ` (${s.count})` : ""}
                </option>
              ))}
            </select>
          </div>
          {(filterDate || filterStatus) && (
            <button
              onClick={() => {
                setFilterDate("");
                setFilterStatus("");
              }}
              className="btn-ghost text-xs"
            >
              ✕ Xoá lọc
            </button>
          )}
          <div className="flex-1" />
          <div className="text-xs text-slate-500 pb-1.5">
            <span className="font-semibold text-slate-900">
              {filtered.length}
            </span>
            /{orders.length} đơn
            {filtered.length > 0 && (
              <>
                {" · "}
                <span className="font-semibold text-emerald-700">
                  {fmt(totalCod)}
                </span>
              </>
            )}
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title={`Chi tiết đơn hàng tháng ${String(month).padStart(2, "0")}/${year}`}
          subtitle={`${orders.length} đơn tổng · max 5000 mới nhất`}
        />
        <div className="overflow-x-auto scrollbar-thin">
          <table className="table-modern">
            <thead>
              <tr>
                <th>Ngày</th>
                <th>Shop</th>
                <th>Đơn POS</th>
                <th>Mã vận đơn</th>
                <th>Khách</th>
                <th>Sản phẩm</th>
                <th className="text-right">COD</th>
                <th className="text-right">Ship</th>
                <th>Trạng thái</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center py-8 text-slate-500">
                    Không có đơn khớp bộ lọc
                  </td>
                </tr>
              )}
              {filtered.map((o) => {
                const st = o.is_partial_return
                  ? { label: "Hoàn 1 phần", tone: "badge-warning" }
                  : STATUS_MAP[o.status] || {
                      label: o.status_name || `#${o.status}`,
                      tone: "badge-neutral",
                    };
                return (
                  <tr key={o.pos_order_id}>
                    <td className="font-mono text-xs">
                      {o.date
                        ? new Date(o.date + "T00:00:00").toLocaleDateString(
                            "vi-VN"
                          )
                        : "—"}
                    </td>
                    <td className="text-xs text-slate-600">{o.shop_name}</td>
                    <td className="font-mono text-xs">{o.pos_order_id}</td>
                    <td className="font-mono text-xs">
                      {o.partner_extend_code || "—"}
                      {o.partner_name && (
                        <div className="text-[10px] text-slate-500">
                          {o.partner_name}
                        </div>
                      )}
                    </td>
                    <td className="text-xs">{o.customer || "—"}</td>
                    <td
                      className="text-xs max-w-xs truncate"
                      title={o.items || ""}
                    >
                      {o.items || "—"}
                    </td>
                    <td className="text-right font-mono font-semibold">
                      {fmt(o.cod)}
                    </td>
                    <td className="text-right font-mono text-slate-500">
                      {o.partner_fee > 0 ? fmt(o.partner_fee) : "—"}
                    </td>
                    <td>
                      <span className={st.tone}>{st.label}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Hiệu suất nhân viên xác nhận đơn
// ─────────────────────────────────────────────────────────────────────────────

type StaffTotal = {
  staff_id: string;
  staff_name: string;
  n_confirmed: number;
  first_at: string | null;
  last_at: string | null;
  avg_hour: number | null;
  peak_hour: number | null;
};

type StaffByDay = {
  date: string;
  staff_id: string;
  staff_name: string;
  n_confirmed: number;
};

type StaffResult = {
  year: number;
  month: number;
  grand_total: number;
  n_staff: number;
  staff_totals: StaffTotal[];
  by_day: StaffByDay[];
};

function StaffTab({
  codeId,
  year,
  month,
}: {
  codeId: number | null;
  year: number;
  month: number;
}) {
  const q = useQuery({
    queryKey: ["staff-performance", codeId, year, month],
    queryFn: async () => {
      const params: Record<string, string | number | string[]> = { year, month };
      if (codeId) params.product_code_id = codeId;
      return (await api.get<StaffResult>("/reports/staff-performance", { params })).data;
    },
  });

  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);
  async function syncStaff() {
    setSyncing(true);
    setSyncMsg(null);
    try {
      const r = await api.post<{ results: { shop_name: string; n_synced?: number }[] }>(
        "/reports/staff-performance/sync-staff"
      );
      const total = r.data.results.reduce((s, x) => s + (x.n_synced || 0), 0);
      setSyncMsg(`✓ Sync ${total} NV từ ${r.data.results.length} shop`);
      await q.refetch();
    } catch (e: any) {
      setSyncMsg(`Lỗi: ${e?.message || "unknown"}`);
    } finally {
      setSyncing(false);
    }
  }

  if (q.isLoading)
    return (
      <Card>
        <CardBody className="text-slate-500 text-sm">Đang tải…</CardBody>
      </Card>
    );
  if (q.error) return <ErrorCard error={q.error} />;
  if (!q.data) return null;

  const { staff_totals, by_day, grand_total, n_staff } = q.data;

  // Pivot by_day → matrix: rows = staff_id, cols = date
  const allDates = Array.from(new Set(by_day.map((r) => r.date))).sort();
  const staffOrder = staff_totals.map((s) => s.staff_id);
  const cellMap = new Map<string, number>();
  for (const r of by_day) cellMap.set(`${r.staff_id}|${r.date}`, r.n_confirmed);

  // Max cell for heatmap intensity
  const maxCell = Math.max(0, ...by_day.map((r) => r.n_confirmed));
  const intensity = (n: number) => {
    if (n <= 0) return "";
    const ratio = n / (maxCell || 1);
    if (ratio > 0.75) return "bg-indigo-600 text-white font-semibold";
    if (ratio > 0.5) return "bg-indigo-400 text-white";
    if (ratio > 0.25) return "bg-indigo-200 text-slate-800";
    return "bg-indigo-50 text-slate-700";
  };

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <KpiCard
          label="Tổng đơn xác nhận"
          value={fmtNumOnly(grand_total)}
          hint={`${n_staff} nhân viên`}
          icon={UserCheck}
          tone="brand"
        />
        <KpiCard
          label="TB / nhân viên"
          value={fmtNumOnly(n_staff > 0 ? Math.round(grand_total / n_staff) : 0)}
          hint="đơn trong tháng"
          icon={Users}
          tone="green"
        />
        <KpiCard
          label="Ngày làm việc"
          value={fmtNumOnly(allDates.length)}
          hint="ngày có xác nhận"
          icon={CalendarDays}
          tone="amber"
        />
        <KpiCard
          label="TB / ngày"
          value={fmtNumOnly(
            allDates.length > 0 ? Math.round(grand_total / allDates.length) : 0
          )}
          hint="đơn xác nhận / ngày"
          icon={TrendingUp}
          tone="brand"
        />
      </div>

      {/* Bảng xếp hạng NV */}
      <Card className="mb-4">
        <CardHeader
          title="Xếp hạng nhân viên"
          subtitle="Đếm mỗi lần status đơn chuyển sang Đã xác nhận (theo giờ VN)."
          action={
            <button
              onClick={syncStaff}
              disabled={syncing}
              className="btn btn-sm btn-outline flex items-center gap-1"
            >
              <RefreshCw
                className={clsx("w-3.5 h-3.5", syncing && "animate-spin")}
              />
              {syncing ? "Đang sync…" : "Sync tên NV"}
            </button>
          }
        />
        {syncMsg && (
          <div className="px-4 pb-2 text-xs text-slate-600">{syncMsg}</div>
        )}
        <div className="overflow-x-auto">
          <table className="table-modern w-full text-sm">
            <thead>
              <tr>
                <th className="text-left">#</th>
                <th className="text-left">Nhân viên</th>
                <th className="text-right">Số đơn xác nhận</th>
                <th className="text-right">Tỉ trọng</th>
                <th className="text-right">Giờ TB</th>
                <th className="text-right">Giờ đông nhất</th>
                <th className="text-left">Xác nhận đầu / cuối tháng</th>
              </tr>
            </thead>
            <tbody>
              {staff_totals.map((s, i) => {
                const pct = grand_total > 0 ? (s.n_confirmed / grand_total) * 100 : 0;
                return (
                  <tr key={s.staff_id}>
                    <td className="text-slate-500">{i + 1}</td>
                    <td>
                      <div className="font-medium">{s.staff_name}</div>
                      <div className="text-[10px] text-slate-400 font-mono">
                        {s.staff_id.slice(0, 8)}…
                      </div>
                    </td>
                    <td className="text-right font-mono font-semibold">
                      {fmtNumOnly(s.n_confirmed)}
                    </td>
                    <td className="text-right">
                      <div className="inline-flex items-center gap-2">
                        <div className="w-16 h-1.5 bg-slate-100 rounded overflow-hidden">
                          <div
                            className="h-full bg-indigo-500"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="font-mono text-xs text-slate-600 w-10 text-right">
                          {pct.toFixed(1)}%
                        </span>
                      </div>
                    </td>
                    <td className="text-right font-mono">
                      {s.avg_hour != null ? `${s.avg_hour}h` : "—"}
                    </td>
                    <td className="text-right font-mono">
                      {s.peak_hour != null
                        ? `${String(s.peak_hour).padStart(2, "0")}:00`
                        : "—"}
                    </td>
                    <td className="text-xs text-slate-600 font-mono">
                      {fmtDT(s.first_at)} → {fmtDT(s.last_at)}
                    </td>
                  </tr>
                );
              })}
              {staff_totals.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center text-slate-400 py-6">
                    Không có đơn nào được xác nhận trong tháng này
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Heatmap ngày × nhân viên */}
      {allDates.length > 0 && staff_totals.length > 0 && (
        <Card>
          <CardHeader
            title="Chi tiết theo ngày"
            subtitle="Số đơn xác nhận / ngày × nhân viên. Ô càng đậm = càng nhiều."
          />
          <div className="overflow-x-auto">
            <table className="text-xs w-full">
              <thead>
                <tr>
                  <th className="text-left px-3 py-2 sticky left-0 bg-white z-10 border-b">
                    Ngày
                  </th>
                  {staffOrder.map((sid) => {
                    const st = staff_totals.find((x) => x.staff_id === sid)!;
                    return (
                      <th
                        key={sid}
                        className="text-center px-2 py-2 border-b whitespace-nowrap"
                        title={st.staff_name}
                      >
                        <div className="font-medium">
                          {st.staff_name.length > 14
                            ? st.staff_name.slice(0, 12) + "…"
                            : st.staff_name}
                        </div>
                      </th>
                    );
                  })}
                  <th className="text-right px-3 py-2 border-b bg-slate-50">Tổng</th>
                </tr>
              </thead>
              <tbody>
                {allDates.map((d) => {
                  const rowTotal = staffOrder.reduce(
                    (s, sid) => s + (cellMap.get(`${sid}|${d}`) || 0),
                    0
                  );
                  return (
                    <tr key={d}>
                      <td className="px-3 py-1.5 sticky left-0 bg-white border-b font-mono text-xs">
                        {d.split("-").reverse().join("/")}
                      </td>
                      {staffOrder.map((sid) => {
                        const n = cellMap.get(`${sid}|${d}`) || 0;
                        return (
                          <td
                            key={sid}
                            className={clsx(
                              "text-center px-2 py-1.5 border-b font-mono",
                              intensity(n)
                            )}
                          >
                            {n > 0 ? n : ""}
                          </td>
                        );
                      })}
                      <td className="text-right px-3 py-1.5 border-b font-mono font-semibold bg-slate-50">
                        {rowTotal > 0 ? fmtNumOnly(rowTotal) : ""}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-slate-50 font-semibold">
                  <td className="px-3 py-2 sticky left-0 bg-slate-50 border-t">
                    Tổng
                  </td>
                  {staffOrder.map((sid) => {
                    const st = staff_totals.find((x) => x.staff_id === sid)!;
                    return (
                      <td
                        key={sid}
                        className="text-center px-2 py-2 border-t font-mono"
                      >
                        {fmtNumOnly(st.n_confirmed)}
                      </td>
                    );
                  })}
                  <td className="text-right px-3 py-2 border-t font-mono text-indigo-700">
                    {fmtNumOnly(grand_total)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Card>
      )}
    </>
  );
}

function fmtDT(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}/${mm} ${hh}:${mi}`;
}

function ErrorCard({ error }: { error: unknown }) {
  return (
    <Card>
      <CardBody className="text-sm text-red-700">
        Lỗi tải dữ liệu: {(error as any)?.message || "unknown"}
      </CardBody>
    </Card>
  );
}
